import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { PaperBG } from "../kits";
import VideoCard from "../kits/LayoutKit/VideoCard";
import BlogList from "./BlogList";
import ZenList from "./ZenList";
import CaseList from "./CaseList";
import Courses from "../components/Courses";

const validTabs = ["youtube", "blog", "zendev", "CaseStudy", "Kurse"];

/**
 * A media blog component that displays various content types through tabs
 * @component
 * @description Renders a tabbed interface for different types of content including YouTube videos,
 * blog posts, case studies, courses, and zen development content. The component handles URL query
 * parameters for tab persistence and manages YouTube video fetching.
 *
 * @uses {useLocation} - React Router hook to access current location
 * @uses {useNavigate} - React Router hook for navigation
 * @uses {useState} - React hook for state management
 * @uses {useEffect} - React hook for side effects
 * 
 * @state {string} activeTab - Currently selected tab
 * @state {Array} youtubeVideos - List of fetched YouTube videos
 * @state {boolean} loading - Loading state for YouTube videos
 * @state {string|null} error - Error message for YouTube fetch failures
 *
 * @returns {JSX.Element} A complete media blog interface with header, sticky navigation,
 * and content area that changes based on the selected tab
 */
const MediaBlog = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    return validTabs.includes(tab) ? tab : "zendev";
  };

  const [activeTab, setActiveTab] = useState(getTabFromQuery);

  useEffect(() => {
    const currentTab = getTabFromQuery();
    if (currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (activeTab !== params.get("tab")) {
      params.set("tab", activeTab);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }

    if (activeTab === "youtube") {
      fetchYoutubeVideos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchYoutubeVideos = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `https://www.googleapis.com/youtube/v3/playlistItems`,
        {
          params: {
            playlistId: "PLXtZktdmGcVe2hLOmekArDhkf12DJ_kvS",
            key: "AIzaSyAeaZWjocSwQ70JMbYk-NU5TrHdVPmv6sk",
            part: "snippet",
            maxResults: 10,
          },
        }
      );

      const validVideos = response.data.items.filter(
        (video) =>
          video.snippet &&
          video.snippet.thumbnails?.high &&
          video.snippet.resourceId?.videoId
      );

      setYoutubeVideos(validVideos);
      setLoading(false);
    } catch (err) {
      console.error("YouTube Fetch Error:", err.response?.data || err.message);
      setError("YouTube Videos konnten nicht geladen werden.");
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (activeTab === "youtube" && loading) {
      return (
        <p className="text-center text-gray-500 font-mono">Wird geladen... </p>
      );
    }

    if (activeTab === "youtube" && error) {
      return <p className="text-center text-red-500 font-mono">{error}</p>;
    }

    switch (activeTab) {
      case "youtube":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {youtubeVideos.map((video) => (
              <VideoCard
                key={video.snippet.resourceId.videoId}
                title={video.snippet.title}
                platform="YouTube"
                content={video.snippet.title}
                mediaUrl={`https://www.youtube.com/watch?v=${video.snippet.resourceId.videoId}`}
                thumbnailUrl={video.snippet.thumbnails.high.url}
              />
            ))}
          </div>
        );
      case "blog":
        return (
          <div className="text-center font-mono text-sm">
            <BlogList />
          </div>
        );
      case "CaseStudy":
        return (
          <div className="text-center font-mono text-sm">
            <CaseList />
          </div>
        );
      case "Kurse":
        return <Courses />; // Einfach die Courses Komponente laden
      case "zendev":
        return <ZenList />;
      default:
        return null;
    }
  };

  return (
    <PaperBG fullHeight>
      {/* Header Section */}
      <div className="pt-32 pb-10 px-6">
        <div className="max-w-3xl mx-auto text-center text-xl mt-10 font-mono">
          <h1>Lern Studio</h1>
          <p className="text-xs font-mono">
            Einblicke, Tipps, & Wissen, Lernen, App Entwicklung
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-10 px-6">
        <div className="max-w-6xl mx-auto">{renderContent()}</div>
      </div>
    </PaperBG>
  );
};

export default MediaBlog;
